import React, { useState } from 'react';
import './Projects.css';
import { 
    FaReact, 
    FaNodeJs, 
    FaExternalLinkAlt, 
    FaGooglePlay, 
    FaApple, 
    FaServer, 
    FaChevronDown, 
    FaChevronUp, 
    FaBolt, 
    FaDatabase, 
    FaShieldAlt, 
    FaChartLine, 
    FaCode,
    FaPython
} from 'react-icons/fa';
import { SiExpress, SiMongodb, SiSocketdotio, SiAmazonwebservices, SiMysql, SiTypescript } from 'react-icons/si';

import { projectsData } from '../data/projectsData';
import { trackEvent } from '../utils/analytics';

const techIconMap = {
    'Node.js': <FaNodeJs />,
    'React': <FaReact />,
    'Next.js': <FaReact />,
    'AWS Cloud': <SiAmazonwebservices />,
    'MySQL': <SiMysql />,
    'WebSockets': <SiSocketdotio />,
    'TypeScript': <SiTypescript />,
    'Express': <SiExpress />,
    'MongoDB': <SiMongodb />,
    'Google Play': <FaGooglePlay />,
    'App Store': <FaApple />,
    'Python': <FaPython />,
    'Google BigQuery': <FaDatabase />,
    'PM2': <FaServer />,
};

const projects = projectsData.map((p) => ({
    ...p,
    techStack: p.techStack.map((t) => ({
        ...t,
        icon: t.icon || techIconMap[t.name] || <FaServer />,
    })),
}));

const Projects = () => {
    const [expandedSpecs, setExpandedSpecs] = useState({});

    const toggleSpecs = (id) => {
        const nextState = !expandedSpecs[id];
        if (nextState) {
            trackEvent('project_architecture_expand', { project_id: id });
        }
        setExpandedSpecs((prev) => ({
            ...prev,
            [id]: nextState,
        }));
    };

    return (
        <section className="projects-section" id="projects">
            <div className="cyber-grid"></div>

            <div className="section-container">
                <div className="section-header">
                    <span className="section-badge">FEATURED WORK &amp; SYSTEMS</span>
                    <h2 className="section-title">Verified Production Systems</h2>
                    <p className="section-subtitle">
                        Architected for high concurrency, real-time streaming, and enterprise reliability. Explore production cloud platforms, GovTech infrastructure, and mobile store apps.
                    </p>
                </div>

                <div className="project-grid">
                    {projects.map((project, index) => {
                        const isExpanded = !!expandedSpecs[project.id];
                        const isGovTech = project.id === 'amritpex-2023';

                        return (
                            <article className={`project-card ${project.featured ? 'flagship' : ''}`} key={project.id || index}>
                                {/* Card Topbar & Production Status */}
                                <div className="project-topbar">
                                    <div className="topbar-left">
                                        <span className="project-status-dot" aria-hidden="true"></span>
                                        <span className="project-category-badge">{project.category}</span>
                                    </div>
                                    <div className="topbar-right">
                                        {project.featured ? (
                                            <span className="flagship-tag">FLAGSHIP SYSTEM</span>
                                        ) : isGovTech ? (
                                            <span className="verified-tag">GOVTECH PLATFORM</span>
                                        ) : (
                                            <span className="verified-tag">ENTERPRISE SYSTEM</span>
                                        )}
                                        {project.isLivePublic && project.demoLink && (
                                            <a
                                                href={project.demoLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="project-icon-cta"
                                                aria-label={`Open live system for ${project.title}`}
                                                title={`Visit Live Platform (${project.demoLink})`}
                                                onClick={() => trackEvent('project_outbound_click', { type: 'live_demo', project_id: project.id })}
                                            >
                                                <span className="live-icon-dot" aria-hidden="true"></span>
                                                <span className="live-icon-text">LIVE</span>
                                                <FaExternalLinkAlt className="live-icon-arrow" />
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <div className="project-body">
                                    <div className="project-title-row">
                                        {project.isLivePublic && project.demoLink ? (
                                            <a
                                                href={project.demoLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="project-title-link"
                                                title={`Visit Live Platform (${project.demoLink})`}
                                            >
                                                <h3 className="project-title">{project.title}</h3>
                                            </a>
                                        ) : (
                                            <h3 className="project-title">{project.title}</h3>
                                        )}
                                        {project.role && (
                                            <div className="project-role-wrap">
                                                <span className="project-role-chip">
                                                    <FaCode className="role-chip-icon" />
                                                    <span>{project.role}</span>
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <p className="project-desc">{project.description}</p>

                                    {/* Structured Telemetry & System Scale Matrix */}
                                    <div className="telemetry-matrix">
                                        <div className="telemetry-header">
                                            <span className="pulse-indicator" aria-hidden="true"></span>
                                            <span className="telemetry-header-title">SYSTEM SCALE &amp; TELEMETRY</span>
                                        </div>
                                        <div className="telemetry-grid">
                                            {project.metrics.split('•').map((item, mIdx) => (
                                                <div key={mIdx} className="telemetry-chip">
                                                    <span className="telemetry-chip-bullet" aria-hidden="true">▸</span>
                                                    <span className="telemetry-chip-text">{item.trim()}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Tech Stack Badges */}
                                    <div className="project-tech-list">
                                        {project.techStack.map((tech, idx) => (
                                            <span className="tech-badge" key={idx}>
                                                <span className="tech-badge-icon">{tech.icon}</span>
                                                <span className="tech-badge-name">{tech.name}</span>
                                            </span>
                                        ))}
                                    </div>

                                    {/* Architecture Deep-Dive Toggle Button */}
                                    <button
                                        type="button"
                                        className={`specs-drawer-toggle ${isExpanded ? 'open' : ''}`}
                                        onClick={() => toggleSpecs(project.id)}
                                        aria-expanded={isExpanded}
                                    >
                                        <span className="specs-toggle-left">
                                            <FaServer className="specs-icon" />
                                            <span>Architecture Deep-Dive</span>
                                            <span className="specs-count-badge">4 Specs</span>
                                        </span>
                                        <span className="specs-toggle-arrow">
                                            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                        </span>
                                    </button>

                                    {/* Architecture Deep-Dive Blueprint Drawer */}
                                    {isExpanded && (
                                        <div className="project-arch-drawer" role="region" aria-label="Architecture specifications">
                                            <div className="arch-drawer-header">
                                                <div className="arch-terminal-dots" aria-hidden="true">
                                                    <span className="terminal-dot red"></span>
                                                    <span className="terminal-dot yellow"></span>
                                                    <span className="terminal-dot green"></span>
                                                </div>
                                                <span className="arch-drawer-filename">{project.file || 'topology.ts'}</span>
                                                <span className="arch-drawer-status">VERIFIED SPECS</span>
                                            </div>

                                            <div className="arch-specs-grid">
                                                <div className="arch-spec-item">
                                                    <div className="arch-spec-header">
                                                        <FaBolt className="arch-spec-icon" />
                                                        <span className="arch-spec-label">Engine / Concurrency:</span>
                                                    </div>
                                                    <span className="arch-spec-value">{project.architecture.concurrency}</span>
                                                </div>

                                                <div className="arch-spec-item">
                                                    <div className="arch-spec-header">
                                                        <FaChartLine className="arch-spec-icon" />
                                                        <span className="arch-spec-label">Throughput / Scale:</span>
                                                    </div>
                                                    <span className="arch-spec-value">{project.architecture.throughput}</span>
                                                </div>

                                                <div className="arch-spec-item">
                                                    <div className="arch-spec-header">
                                                        <FaDatabase className="arch-spec-icon" />
                                                        <span className="arch-spec-label">Data Layer:</span>
                                                    </div>
                                                    <span className="arch-spec-value">{project.architecture.dataTier}</span>
                                                </div>

                                                <div className="arch-spec-item">
                                                    <div className="arch-spec-header">
                                                        <FaShieldAlt className="arch-spec-icon" />
                                                        <span className="arch-spec-label">Security &amp; Resilience:</span>
                                                    </div>
                                                    <span className="arch-spec-value">{project.architecture.security}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Projects;